import { Flex, ProviderConsumer as FluentUIThemeConsumer } from '@fluentui/react-northstar';
import { clone } from "lodash";
import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { checkInTeams } from '../App';
import { appState, useSetState } from '../AppState';
import { Nav } from '../browser-pages/browser-components/Nav';
import { SideNav } from '../browser-pages/browser-components/SideNav';
import { TikitConfigState } from '../config/TikitConfiguration';
import { getCachedFeature } from '../shared/cache/FeatureCache';
import { getCachedUser } from '../shared/cache/PlatformUserCache';
import { ThemeColorScheme } from '../shared/common/TeamsTheme';
import { AccessDenied } from '../shared/components/AccessDenied';
import { CheckLogin } from '../shared/components/CheckLogin';
import { FileProps } from '../shared/components/EditTicketAttachments/EditTicketAttachments';
import { getAllMergedTicketIds, IRelatedTickets } from '../shared/components/EditTicketRelateTickets/RelateTicketHelper';
import { EditTicketsView } from '../shared/components/EditTicketsView/EditTicketsView';
import { BreadCrumbItemProps } from '../shared/components/EditTicketToolbar';
import { EditTicketSkeleton } from '../shared/components/skeletonloaders/EditTicketSkeleton';
import { TopBarSkeleton } from '../shared/components/skeletonloaders/TopBarSkeleton';
import { UpgradeNeeded } from '../shared/components/UpgradeNeeded';
import { graphService } from '../shared/services/graph.service';
import { platformService } from '../shared/services/platform.service';
import { ADMINISTRATORS, AGENTS, ANALYSTS, ConfigurationName, FILTERS_STRINGS, LICENSE_FEATURE, PRODUCT_LICENSE, STATUS_GUID, toastDefault } from '../shared/utils/constants';
import { safeJsonParse } from '../shared/utils/helper';
import { retrieveTicketInformation } from '../shared/utils/propertyHelper';
import { updateIsCustomFormSavingState } from '../templates/CustomForms/CustomForm.Service';
import { buildBreadcrumbItems } from './EditTicket/breadcrumb';
import { ACTIONS, editPageInitialState, editPageReducer } from './EditTicket/editTicketState';
import { PaneHandle } from '../shared/components/PaneHandle/PaneHandle';
import { EDIT_TICKET_STORAGE_KEYS, getPaneFlag, setPaneFlag } from './EditTicket/storageKeys';
import { useCommentsCount } from './EditTicket/hooks/useCommentsCount';
import { useSideViewTickets } from './EditTicket/hooks/useSideViewTickets';
import { useTicketMenuItems } from './EditTicket/hooks/useTicketMenuItems';
import { TicketDetailCard } from './EditTicket/TicketDetailCard';
import { TicketEditPanel } from './EditTicket/TicketEditPanel';
import { TicketTabsContent } from './EditTicket/TicketTabsContent';
import { useDiscardDraftOnLeave } from '../shared/components/TicketConversation/useDiscardDraftOnLeave';
import { ContentMenu, TicketToolbarSection } from './EditTicket/TicketToolbarSection';
import { EditTicketEvent, loadFilters, threedigitized } from './ticketHelper';
import { TicketPagePrint } from './TicketPagePrint/TicketPagePrint';

export const SingleTicketView: React.FC = () => {
  const { t } = useTranslation();
  const api = useMemo(() => new platformService(), []);
  const graphAPI = useMemo(() => new graphService(), []);
  const failedApiCountRef = useRef(0);
  const [isInTeam, setIsInTeam] = useState(checkInTeams() ? 'height--50' : `height--100`);
  const [state, dispatch] = useReducer(editPageReducer, editPageInitialState);
  const history = useHistory();
  const [reloadConversation, setReloadConversation] = useState<boolean>(false);
  const onCommentUpdated = useCallback(() => {
    setReloadConversation(true);
  }, []);
  const setAppState = useSetState();
  const currentState = appState();
  const [isUserLicensed, setIsUserLicensed] = useState<boolean>(false);
  const [isLifecycleEnabled, setIsLifecycleEnabled] = useState<boolean>(false);
  const [tikitConfig, setTikitConfig] = useState<TikitConfiguration>(TikitConfigState);
  const [isResolutionNoteEnabled, setIsResolutionNoteEnabled] = useState(false);
  const [isTimeTrackingEnabledByConfig, setIsTimeTrackingEnabledByConfig] = useState(false);

  const [mergedTicketIds, setMergedTicketIds] = useState<number[]>([]);

  const { ticketId } = useParams<any>();
  // loadRequest counts reloads, so asking for the ticket already open still loads it: a
  // freshly opened ticket never sets isTicketLoaded, so nothing else would change.
  const [ticketState, setTicketState] = useState({ ticketId: ticketId, isSelectItem: false, isTicketLoaded: false, loadRequest: 0 });
  // keepTab: a Refresh from the toolbar loads the ticket again on the tab it is on; opening a ticket starts on Activity.
  const [selectedTicket, setSelectedTicket] = useState({ ticketId: ticketId, reload: false, keepTab: false });
  const [isAuthorizedAccess, setIsAuthorizedAccess] = useState(true);
  const [detailCardApprovalsList, SetDetailCardApprovalsList] = useState<TicketApprovals[]>([])
  const [detailCardTaskList, SetDetailCardTaskList] = useState<TicketTask[]>([]);
  const [droppedIndex, setDroppedIndex] = useState<number>(1);
  const [isCustomViewEnabled, setIsCustomViewEnabled] = useState(false);
  const [isScopingEnabled, setIsScopingEnabled] = useState(false);
  const [sortedViews, setSortedViews] = useState([]);
  const [isEditPanelCollapsed, setIsEditPanelCollapsed] = useState(() => getPaneFlag(EDIT_TICKET_STORAGE_KEYS.editPanelCollapsed));
  const toggleEditPanel = useCallback(() => {
    setIsEditPanelCollapsed(prev => {
      setPaneFlag(EDIT_TICKET_STORAGE_KEYS.editPanelCollapsed, !prev);
      return !prev;
    });
  }, []);

  const {
    activeDropdownIndex: activeDrodpdownIndex,
    setActiveDropdownIndex: handleSetActiveDrodpdownIndex,
    sideViewTicketsState,
    setSideViewTicketsState,
    filterSideViewTicketsOnTicketSelection
  } = useSideViewTickets({ api, ticketId, ticketStatus: currentState.ticketStatus, isCustomViewEnabled, sortedViews });

  const { commentsCount, setCommentsCount, fetchCommentsCount: setTicketCommentsCount } = useCommentsCount(api, ticketState.ticketId);

  const onReloadPhasesRef = useRef<() => Promise<void> | void>(() => undefined);
  const stableOnReloadPhases = useCallback(() => onReloadPhasesRef.current(), []);

  // Time tracking is for Admins, Super Agents (the "Analysts" role) and Agents only.
  // Deliberately not currentState.userRoles.isAuthorizedAgent, which also covers
  // Knowledge Agents and Knowledge Readers.
  const isTimeTrackingRole = useMemo(() => {
    const roles = currentState?.userRoles?.roles ?? [];
    return roles.includes(ADMINISTRATORS) || roles.includes(ANALYSTS) || roles.includes(AGENTS);
  }, [currentState?.userRoles?.roles]);

  const { menu, setMenu, menuItems, menuIndexChange } = useTicketMenuItems({
    ticket: state.ticket,
    ticketStatus: currentState.ticketStatus,
    isLifecycleEnabled,
    isResolutionNoteEnabled,
    isResolutionNoteEnabledByConfig: tikitConfig.IsResolutionNoteEnabled,
    isTimeTrackingVisible: isTimeTrackingEnabledByConfig && isTimeTrackingRole,
    lifecycleMenuOpen: currentState?.lifecycleMenuOpen ?? false,
    lifecyclePhaseMenu: currentState.lifecyclePhaseMenu,
    setAppState,
    onReloadPhases: stableOnReloadPhases,
    t
  });

  // Functions
  const loadFiltersData = async () => {
    try {
      const newState = await loadFilters(api, graphAPI, currentState, setAppState);
      failedApiCountRef.current = 0;
      return newState;
    } catch (error) {
      if (++failedApiCountRef.current <= 5) loadFiltersData();
      console.error(error);
    }
  };

  const getPopulatedTicket = async (ticketId: number, filter: string): Promise<Ticket> => {
    const [{ data }, index] = await Promise.all([
      api.getTicket(ticketId, filter),
      getLatestDroppedIndex(ticketId)
    ]);
    if (data) {
      setDroppedIndex(index);
      const cardAnswer = data?.CardAnswerJson ?? "{}";
      const cardAnswerJson = safeJsonParse(cardAnswer.length > 0 ? cardAnswer : "{}");
      const cardTemplate = data?.CardTemplateJson ?? "{}";
      const cardTemplateJson = safeJsonParse(cardTemplate.length > 0 ? cardTemplate : "{}");
      setAppState((s) => ({
        ...s,
        cardAnswerJSON: cardAnswerJson,
        cardTemplateJSON: cardTemplateJson,
      }));
    }

    return await retrieveTicketInformation(data, currentState, true);
  }

  const loadPage = async () => {
    try {

      if (sortedViews.length<=0) {
        const _isCustomViewsEnabled = await getCachedFeature(LICENSE_FEATURE.CustomViews);
        if (_isCustomViewsEnabled){
          setIsCustomViewEnabled(_isCustomViewsEnabled);

        const _isScopingEnabled = await getCachedFeature(LICENSE_FEATURE.PermissionScoping);
        setIsScopingEnabled(_isScopingEnabled);

          const customViewsList = await api.getCustomViewsSafe('?$expand=ViewFilters(select=Id,FilterKey,FilterValues,NumberValue,StringValue),ScopedMembers(select=Member,AadObjectId,ScopedType,CreatedById)');
          setSortedViews((customViewsList ?? []).sort((a, b) => a.Name.localeCompare(b.Name)));
        }
      }

      const isRelateTicketEnabled = await getCachedFeature(LICENSE_FEATURE.RelatedTickets);
      const results = await Promise.all([
        (currentState?.ticketStatus?.length > 0) ? Promise.resolve(currentState) : loadFiltersData(),
        getPopulatedTicket(ticketState.ticketId, FILTERS_STRINGS.TICKET_DETAILS),
        isRelateTicketEnabled ? api.getAllMergedTickets() : Promise.resolve([]),
        getCachedFeature(PRODUCT_LICENSE.AnalystAccess),
        getCachedFeature(LICENSE_FEATURE.TicketLifecycle),
        api.getTeamsBotConfiguration(),
        api.getTicketCommentCount(ticketState.ticketId),
        api.getSystemSetting(ConfigurationName.Resolution),
        getCachedFeature(LICENSE_FEATURE.ResolutionNote),
        getCachedUser(currentState.currentUserId),
        getCachedFeature(LICENSE_FEATURE.TimeTracking)
      ]);
      setAppState(prevState => ({ ...prevState, userImages: {}, currentUserEmail: currentState.userRoles.roles[0], teamsConfig: results[5].data }));

      dispatch({ type: ACTIONS.SET_FILE_CHANNEL, data: results[5].data });

      setIsUserLicensed(results[3]);
      setIsLifecycleEnabled(results[4]);

      const requester = results[1].Requester;
      const me = results[9];

      dispatch({ type: ACTIONS.SET_DEFAULT_TICKET, data: clone(results[1]) });
      dispatch({ type: ACTIONS.SET_TICKET, data: results[1] });

      // The side-view list is fetched once per mount and thereafter only patched locally,
      // so a change made in another session never reaches it - the clicked card would keep
      // showing whatever was true when the list loaded. The detail we just fetched IS
      // current, so reuse it to correct that one row. No extra request.
      //
      // Every field the card renders is listed explicitly and defaulted to null, because
      // retrieveTicketInformation omits Assignee and Requester entirely when their ids are
      // null, and the merge is a spread - which cannot delete a key. See the onUnassign
      // branch for the same trap.
      filterSideViewTicketsOnTicketSelection({
        StatusId: results[1].StatusId ?? null,
        AssigneeId: results[1].AssigneeId ?? null,
        SupportGroupId: results[1].SupportGroupId ?? null,
        Title: results[1].Title ?? null,
        CreatedDate: results[1].CreatedDate ?? null,
        Assignee: results[1].Assignee ?? null,
        Requester: results[1].Requester ?? null
      });
      dispatch({ type: ACTIONS.SET_SIGNEDIN_USER, data: me });
      dispatch({ type: ACTIONS.SET_REQUESTER, data: requester });
      dispatch({ type: ACTIONS.SUCCESS });
      setIsAuthorizedAccess(true);
      const teamsConfig = results[5].data;
      const resolutionConfig = (results[7]?.data ?? {}) as Record<string, unknown>;
      setTikitConfig({
        ...TikitConfigState,
        ...resolutionConfig,
        IsResolutionNoteEnabled: Boolean(resolutionConfig.IsEnabled),
        AgentsCanEditTheirOwnComments:
          teamsConfig?.AgentsCanEditTheirOwnComments ?? TikitConfigState.AgentsCanEditTheirOwnComments,
        AgentsCanDeleteTheirOwnComments:
          teamsConfig?.AgentsCanDeleteTheirOwnComments ?? TikitConfigState.AgentsCanDeleteTheirOwnComments,
      });
      setIsResolutionNoteEnabled(results[8]);
      // One flag, already combining licence and tenant setting - see GetAvailableFeatures.
      // Read from the cache rather than fetched, so this costs no round trip; it carries a
      // TTL so an admin toggling the setting is picked up without a re-login.
      setIsTimeTrackingEnabledByConfig(results[10] ?? false);

      if (ticketState.isSelectItem) {
        setTicketState({ ...ticketState, isSelectItem: false, isTicketLoaded: true });
      }

      const mergedTicketIdsList = isRelateTicketEnabled ? getAllMergedTicketIds(ticketState.ticketId, results[2]) : [];
      setMergedTicketIds(mergedTicketIdsList);
      if (mergedTicketIdsList && mergedTicketIdsList.length > 0) setTicketCommentsCount(mergedTicketIdsList);
      else setCommentsCount(results[6] ?? 0);

    } catch (e) {
      const errCode = e['response']?.status;
      if (errCode == 404) setIsAuthorizedAccess(false);
      console.error(e);
    }
  };

  const fixHeight = () => {
    setIsInTeam(checkInTeams() ? 'height--100' : 'height--150');
  };
  const resetCommentsCount = useCallback(() => {
    setTicketCommentsCount(mergedTicketIds);
  }, [setTicketCommentsCount, mergedTicketIds]);

  const breadcrumbItems = useMemo<BreadCrumbItemProps[]>(
    () => buildBreadcrumbItems(history.location.state as { from?: string } | undefined, t),
    [history.location.state, t]
  );

  const onReloadPhases = useCallback(async () => {
    const data = await getPopulatedTicket(ticketId, FILTERS_STRINGS.TICKET_DETAILS);
    dispatch({ type: ACTIONS.SET_TICKET, data });
  }, [ticketId]);

  onReloadPhasesRef.current = onReloadPhases;

  const onToolbarInteraction = useCallback(async (action: any) => {
    if (action.event === 'saveTicket') dispatch({ type: ACTIONS.SAVING_STATE });
    if (action.event === 'takeTicket') {
      dispatch({ type: ACTIONS.TAKING_TICKET });
      filterSideViewTicketsOnTicketSelection(action.data);
      dispatch({ type: ACTIONS.SET_TICKET, data: action.data });
    }
    if (action.event === 'onUnassign') {
      dispatch({ type: ACTIONS.UNASSIGNING });
      // retrieveTicketInformation only sets Assignee when AssigneeId is truthy, so an
      // unassigned ticket comes back with no Assignee key at all. The side-view merge is
      // a spread, which cannot delete a key - null it explicitly or the card keeps the
      // previous assignee.
      filterSideViewTicketsOnTicketSelection({ ...action.data, AssigneeId: null, Assignee: null });
      dispatch({ type: ACTIONS.SET_TICKET, data: action.data });
    }
    if (action.event === 'onEdit') dispatch({ type: ACTIONS.EDITTING });
    // Through the route, as the side list's card does: the page's leave prompt guards unsaved edits, and the ticket
    // follows location.key. A replace, so Back does not step through refreshes.
    if (action.event === 'refresh') history.replace({ pathname: window.location.pathname, search: window.location.search, state: { keepTab: true } });
    if (action.event === 'onApplyTemplate')
      dispatch({ type: ACTIONS.APPLY_TEMPLATE });
    if (action.event === 'onApplyLifecycle')
      dispatch({ type: ACTIONS.APPLY_LIFECYCLE });
    if (action.event === 'set-ticket-SLAs') {
      dispatch({
        type: ACTIONS.SET_TICKET_SLAS,
        data: action.data
      });
    }
    if (action.event === ACTIONS.PULL_GROUPCHAT) {
      dispatch({
        type: ACTIONS.PULL_GROUPCHAT,
        data: action.data
      });
    }
    if (action.event === 'onSendTemplateForm')
      dispatch({ type: ACTIONS.SEND_TEMPLATEFORM });
  }, [filterSideViewTicketsOnTicketSelection]);

  const saveComplete = useCallback(async (action: any) => {
    if (action.event === EditTicketEvent.SaveSuccess || action.event === EditTicketEvent.DeleteSuccess || action.event === EditTicketEvent.CustomFormUpdated) {

      const data = await getPopulatedTicket(ticketId, FILTERS_STRINGS.TICKET_DETAILS);
      filterSideViewTicketsOnTicketSelection(data);
      dispatch({ type: ACTIONS.SET_TICKET, data });

      dispatch({ type: ACTIONS.SAVING_SUCCESS });
      if (action.event !== EditTicketEvent.CustomFormUpdated)
        toast.success(action.event === EditTicketEvent.DeleteSuccess ? t('ticket-details.edit-ticket-view.file-deleted') : t('ticket-details.edit-ticket-view.ticket-updated-successfully', { ticketId: ticketId }), toastDefault);
      dispatch({ type: ACTIONS.DONE_EDITTING });
      setReloadConversation(true);

      if (action.event === EditTicketEvent.CustomFormUpdated) {
        updateIsCustomFormSavingState(false, setAppState);
        dispatch({ type: ACTIONS.SET_RELOAD_ATTACHMENTS, data: action.data?.hasUploaded ?? false });
      }
    }
    if (action.event === EditTicketEvent.TicketUnassigned) {
      dispatch({ type: ACTIONS.UNASSIGNING_SUCCESS });
      toast.success(t('ticket-details.edit-ticket-view.ticket-updated-successfully', { ticketId: ticketId }), toastDefault);
    }
    if (action.event === EditTicketEvent.TicketTaken) {
      dispatch({ type: ACTIONS.TAKING_TICKET_SUCCESS });
      toast.success(t('ticket-details.edit-ticket-view.ticket-updated-successfully', { ticketId: ticketId }), toastDefault);
    }
    if (action.event === EditTicketEvent.TemplateApplied) {
      dispatch({ type: ACTIONS.APPLY_TEMPLATE_SUCCESS });

      if (action.event === 'onApplyLifecycleSuccess')
        dispatch({ type: ACTIONS.APPLY_LIFECYCLE_SUCCESS });

      if (action.event === 'onApplyLifecycleToTicket')
        dispatch({ type: ACTIONS.APPLY_LIFECYCLE_TICKET, data: action.data });
    }

    if (action.event === 'onAttachmentsUpdate') {
      setReloadConversation(true);
    }
    if (action.event === EditTicketEvent.TicketsRelated) {
      const mergedTicketIdsList = getAllMergedTicketIds(ticketState.ticketId, (action.data as IRelatedTickets[]));
      setMergedTicketIds(mergedTicketIdsList);
      const results = await Promise.all([
        getPopulatedTicket(ticketId, FILTERS_STRINGS.TICKET_DETAILS),
        setTicketCommentsCount(mergedTicketIdsList)
      ]);
      if (results[0]) {
        dispatch({ type: ACTIONS.SET_TICKET, data: { ...state.ticket, ...results[0] } });
      }
    }
  }, [ticketId, ticketState.ticketId, state.ticket, t, filterSideViewTicketsOnTicketSelection, setAppState, setTicketCommentsCount]);

  // Effects
  useEffect(() => {
    if (!ticketState.isTicketLoaded) {
      loadPage();
    }
  }, [ticketState.ticketId, ticketState.isTicketLoaded, ticketState.loadRequest]);

  // The ticket follows the route: each new history entry loads the ticket it names,
  // including the open one when its card is clicked again. Loading on the card's click
  // instead went ahead even when the route change was refused - by the prompt for an
  // unsent comment - and left Back and Forward changing the address but not the ticket.
  const location = useLocation();
  const firstLocation = useRef(true);
  useEffect(() => {
    if (firstLocation.current) {
      firstLocation.current = false;
      return;
    }
    if (ticketId) setSelectedTicket({ ticketId, reload: true, keepTab: !!(location.state as { keepTab?: boolean } | undefined)?.keepTab });
  }, [location.key]);
  useDiscardDraftOnLeave();

  useEffect(() => {
    if (selectedTicket.reload) {
      setAppState(prevState => ({ ...prevState, lifecycleMenuOpen: false, lifecyclePhaseMenu: undefined }));
      window.history.replaceState({}, 'tickets', `${window.location.origin}/tickets/${selectedTicket.ticketId}`);
      // React 16 does not auto-batch async state updates inside useEffect.
      // Wrap in unstable_batchedUpdates so the ticket-switch cascade triggers
      // a single re-render instead of six.
      ReactDOM.unstable_batchedUpdates(() => {
        setTicketState(prev => ({ ticketId: selectedTicket.ticketId, isSelectItem: true, isTicketLoaded: false, loadRequest: prev.loadRequest + 1 }));
        setSelectedTicket({ ...selectedTicket, reload: false });
        setCommentsCount(0);
        if (!selectedTicket.keepTab) setMenu(0);
        SetDetailCardApprovalsList([]);
        SetDetailCardTaskList([]);
      });
    }
  }, [selectedTicket]);


  useEffect(() => {
    if (isLifecycleEnabled && state.ticket.TicketLifecycleId && currentState.isRouteFromMyWork) {
      setAppState(prevState => ({ ...prevState, lifecycleMenuOpen: true, isRouteFromMyWork: false }));
    }
  }, [isLifecycleEnabled, state.ticket.TicketLifecycleId]);

  const onSendComment = useCallback(async (date: Date) => {
    const updatedTicket = { ...state.ticket, ModifiedDate: date.toISOString() }
    dispatch({ type: ACTIONS.SET_TICKET, data: updatedTicket });
  }, [state.ticket]);

  const getLatestDroppedIndex = async (id: number): Promise<number> => {
    try {
      const response: any = await api.getTicketFiles(id);
      let idx = 0;

      for (const file of response) {
        if (file.FileName.indexOf('image-') > -1) {
          const indexNumber = parseInt(file.FileName.substring(file.FileName.indexOf('image-') + 'image-'.length, file.FileName.indexOf('image-') + 'image-'.length + 3));
          if (indexNumber > idx)
            idx = indexNumber;
        }
      }

      return ++idx;

    } catch {
      return 1;
    }
  };

  const onTicketUpdate = useCallback(async (data: any, date: Date) => {
    const status = currentState.ticketStatus.find(status => status.Id === data.StatusId);
    const updatedTicket = {
      ...state.ticket, ...data,
      Assignee: await getCachedUser(data.AssigneeId),
      Status: status,
      // Everything that locks a closed ticket reads Closed, which only the server sets; this is its rule.
      Closed: status ? [STATUS_GUID.CLOSED, STATUS_GUID.DEFLECTED].includes(status.Guid) : state.ticket.Closed,
      Priority: currentState.priority.find(priority => priority.Id === data.PriorityId),
      TicketSlas: data?.slaList ?? state.ticket.TicketSlas,
      ModifiedDate: date.toISOString()
    }
    dispatch({ type: ACTIONS.SET_TICKET, data: updatedTicket });
    filterSideViewTicketsOnTicketSelection(updatedTicket);
    toast.success(t('ticket-details.edit-ticket-view.ticket-updated-successfully', { ticketId: threedigitized(updatedTicket.Id) }), toastDefault);
    dispatch({ type: ACTIONS.RESET_UPDATE_TICKET, data: true });
  }, [state.ticket, currentState.ticketStatus, currentState.priority, t, filterSideViewTicketsOnTicketSelection]);
  const [uploadedFiles, setUploadedFiles] = useState<FileProps[]>([]);

  let edittingFormClass = state.isEditting ? `editting-state-form` : ``
  return (
    <CheckLogin onBillingIssue={fixHeight}>
      <Helmet>
        <title>{t('tab-title.ticket')} #{threedigitized(ticketState.ticketId)}{((state?.ticket?.Title || '') != '' ? `: ${state.ticket.Title}` : '')} - {t('tab-title.postfix')}</title>
      </Helmet>
      <FluentUIThemeConsumer
        render={globalTheme => (
          <div className={`overflow-hidden flex flex-col print-block`}>
            {state.isLoading && isAuthorizedAccess && !ticketState.isSelectItem && (
              <EditTicketSkeleton />
            )}
            {(!state.isLoading || ticketState.isSelectItem) && (
              <>
                <TicketPagePrint ticket={state.ticket} ticketState={ticketState} globalTheme={globalTheme} />
                <TicketToolbarSection
                  state={state}
                  ticketId={ticketState.ticketId}
                  currentUserId={currentState.currentUserId}
                  isItemSelected={ticketState.isSelectItem}
                  isUserLicensed={isUserLicensed}
                  mergedTicketIds={mergedTicketIds}
                  breadcrumbItems={breadcrumbItems}
                  globalTheme={globalTheme}
                  onToolbarInteraction={onToolbarInteraction}
                />
                <>
                  <Flex
                    className={`md:h-full flex flex-col md:flex-row flex-1 overflow-hidden relative`}
                    style={{ height: 'auto', ...ThemeColorScheme(globalTheme.siteVariables) }}
                  >

                    <Flex.Item>
                      <EditTicketsView
                        selectedTicketId={ticketState.ticketId}
                        // The card's link changes the route, and the effect on the location
                        // loads the ticket - nothing to do on the click itself.
                        onTicketClick={() => undefined}
                        activeDrodpdownIndex={activeDrodpdownIndex}
                        setActiveDrodpdownIndex={handleSetActiveDrodpdownIndex}
                        ticketsState={sideViewTicketsState}
                        setTicketsStateHandler={setSideViewTicketsState}
                        globalTheme={globalTheme}
                        isCustomViewEnabled={isCustomViewEnabled}
                        sortedViews={sortedViews}
                        currentUser={currentState.me}
                        isScopingEnabled={isScopingEnabled}
                      />
                    </Flex.Item>
                    {!ticketState.isSelectItem ? (
                      <Flex.Item styles={{ flex: '1' }}>
                        <Flex column className={`w-0 pt-2 md:pt-5 pr-2 md:pr-5 pl-0 relative flex flex-col edit-page ${isInTeam}`}>
                          {!state.isLoading && (
                            <TicketDetailCard
                              commentsCount={commentsCount}
                              attachmentsCount={currentState.uploadingState.uploadedFiles.length}
                              ticketData={state.ticket}
                              showViewMore
                              detailCardApprovalsList={detailCardApprovalsList}
                              detailCardTaskList={detailCardTaskList}
                              isTimeTrackingEnabled={isTimeTrackingEnabledByConfig}
                              agentTimeUpdate={state.agentTimeUpdate}
                              collapsible
                            />
                          )}
                          {/* In the column's right gutter, against the panel's edge, so it is
                              in the same place whether the panel is open or a rail. */}
                          <div className="hidden md:block print-hidden" style={{ position: 'absolute', right: '2px', top: 'calc(50% - 24px)', zIndex: 1 }}>
                            <PaneHandle
                              direction={isEditPanelCollapsed ? 'left' : 'right'}
                              label={t(isEditPanelCollapsed ? 'ticket-details.panes.expand-properties' : 'ticket-details.panes.collapse-properties')}
                              onClick={toggleEditPanel}
                            />
                          </div>
                          <Flex.Item key={menu}>
                            <ContentMenu menu={menu} menuItems={menuItems} menuIndexChange={menuIndexChange} />
                          </Flex.Item>
                          <TicketTabsContent
                            activeMenuItem={menuItems[menu]}
                            state={state}
                            ticketId={ticketId}
                            isUserLicensed={isUserLicensed}
                            isResolutionNoteEnabled={isResolutionNoteEnabled}
                            tikitConfig={tikitConfig}
                            currentState={currentState}
                            globalTheme={globalTheme}
                            droppedIndex={droppedIndex}
                            reloadConversation={reloadConversation}
                            uploadedFiles={uploadedFiles}
                            dispatch={dispatch}
                            setAppState={setAppState}
                            setUploadedFiles={setUploadedFiles}
                            setReloadConversation={setReloadConversation}
                            onCommentUpdated={onCommentUpdated}
                            resetCommentsCount={resetCommentsCount}
                            onSendComment={onSendComment}
                            onTicketUpdate={onTicketUpdate}
                            onReloadPhases={onReloadPhases}
                            onToolbarInteraction={onToolbarInteraction}
                            saveComplete={saveComplete}
                          />
                        </Flex>
                      </Flex.Item>
                    ) : (
                      <Flex.Item styles={{ height: 'calc(100vh - 50px)', flex: '1' }}>
                        <Flex column className={`pt-5 px-5 relative flex flex-col`}>
                          <Flex.Item>
                            <TopBarSkeleton />
                          </Flex.Item>
                        </Flex>
                      </Flex.Item>
                    )}
                    <TicketEditPanel
                      state={state}
                      globalTheme={globalTheme}
                      isUserLicensed={isUserLicensed}
                      tikitConfig={tikitConfig}
                      isItemSelected={ticketState.isSelectItem}
                      edittingFormClass={edittingFormClass}
                      dispatch={dispatch}
                      setSideViewTicketsState={setSideViewTicketsState}
                      SetDetailCardApprovalsList={SetDetailCardApprovalsList}
                      SetDetailCardTaskList={SetDetailCardTaskList}
                      saveComplete={saveComplete}
                      onToolbarInteraction={onToolbarInteraction}
                      collapsed={isEditPanelCollapsed}
                    />
                  </Flex>
                </>
              </>
            )}
            {!isAuthorizedAccess && (
              <AccessDenied />
            )}
          </div>
        )}
      />
    </CheckLogin>
  );
};

export const EditTicket: React.FC = () => {
  const setState = useSetState();
  const history = useHistory();
  const { t } = useTranslation();

  useEffect(() => {
    setState(state => {
      let newstate = { ...state, isLoading: true };
      return newstate;
    });
  }, []);
  let state: any = history.location.state || { from: 'tickets' };
  const active = state.from === '' ? 'tickets' : state.from;

  const [isFeatureEnabled, setIsFeatureEnabled] = useState(true);
  (async () => setIsFeatureEnabled((await getCachedFeature(LICENSE_FEATURE.TicketPortal))))();

  return isFeatureEnabled ? (
    <>
      {checkInTeams() && <SingleTicketView />}
      {!checkInTeams() && (
        <>
          <Nav />
          <div className={'overflow-hidden flex flex-col md:block w-full flex-1'}>
            <div className={'flex overflow-hidden flex-col md:flex-row flex-1 print-block'}>
              <div className="hidden md:flex">
                <SideNav
                  active={active}
                  hideSettingsNav={true}
                />
              </div>
              <div className={'w-full flex-1 flex flex-col overflow-hidden print-block'}>
                <SingleTicketView />
              </div>
            </div>
          </div>
        </>
      )}
    </>
  ) : <UpgradeNeeded
    toolbarText={t('ticket.toolbar.tickets')}
    headerText={t('ticket-portal.no-access-current-plan')}
    subheaderText={t('ticket-portal.no-access-upgrade-plan')} />
};
